//External libraries
import * as React from "react";
import { LocalizedText } from "bernie-l10n";
import { useLocalization } from "@shared-ui/localization-context";

// Uitk
import { UitkCard, UitkCardLink } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";

// Components-Utilities
import { PriceContent } from "./PriceContent";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";

// Types
import { RecentlyViewedHotelsCardProps } from "../typings";
import { CustomerSearchHistoryQuery } from "__generated_deprecated__/api";
import HistoryPropertyDetailInlineFragment = CustomerSearchHistoryQuery.HistoryPropertyDetailInlineFragment;

enum VariationType {
  PRICE_INCREASE = "increase",
  PRICE_DECREASE = "decrease",
  AVAILABLE = "available",
  SOLD_OUT = "sold_out",
}

export const RecentlyViewedHotelCard = (props: RecentlyViewedHotelsCardProps) => {
  const { formatText, formatDateString } = useLocalization();
  const { cardIndex, recentlyViewedHotel } = props;

  const {
    startDate,
    endDate,
    originalSearchPrice,
    productInfo,
    hisUrl,
    newPriceFormatted,
    priceChange,
  } = recentlyViewedHotel;

  const dateFormat = "MMMd";
  const checkInDateFormatted = formatDateString(startDate.raw, { skeleton: dateFormat });
  const checkOutDateFormatted = formatDateString(endDate.raw, { skeleton: dateFormat });
  const locKeyIncreasePrice = "recentlyViewedHotels.increasePrice";
  const locKeyDecreasePrice = "recentlyViewedHotels.decreasePrice";
  const locKeyWhenBooking = "hotels.whenBooking";

  const getPriceVariation = () => {
    let priceMessage = "";
    const priceToShow = newPriceFormatted || originalSearchPrice?.formatted;

    if (priceChange) {
      switch (priceChange) {
        case VariationType.SOLD_OUT: {
          return { isSoldOut: true };
        }
        case VariationType.PRICE_INCREASE: {
          priceMessage = formatText(locKeyIncreasePrice, originalSearchPrice?.formatted);
          break;
        }
        case VariationType.PRICE_DECREASE: {
          priceMessage = formatText(locKeyDecreasePrice, originalSearchPrice?.formatted);
          break;
        }
        default: {
          priceMessage = formatText("hotels.price.perNight");
          break;
        }
      }
    }

    return { isSoldOut: false, priceToShow, priceMessage };
  };

  const properties = getPriceVariation();
  const productInformation = productInfo as HistoryPropertyDetailInlineFragment;

  return (
    <UitkCard className="carousel-item" border>
      <UitkLayoutGrid rows={1} columns={4} data-testid={`recently-viewed-card-${cardIndex}`}>
        <UitkLayoutGridItem colSpan={1}>
          <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
            <UitkImage
              src={productInformation?.propertyImageGallery?.featured?.image?.url}
              alt={productInformation?.propertySummary?.name}
              placeholderImage
              lazyLoading="lazy"
            />
          </UitkFigure>
        </UitkLayoutGridItem>
        <UitkLayoutGridItem colSpan={3}>
          <UitkSpacing margin="three">
            <UitkLayoutFlex direction="column" justifyContent="space-between">
              <UitkSpacing margin={{ blockend: "three" }}>
                <UitkHeading overflow="truncate-2-lines" tag="h3" size={5}>
                  {productInformation?.propertySummary?.name}
                </UitkHeading>
              </UitkSpacing>
              <div>
                <PriceContent
                  isSoldOut={properties.isSoldOut}
                  priceToShow={properties.priceToShow}
                  priceMessage={properties.priceMessage}
                />
                <UitkText size={100}>
                  {formatText(locKeyWhenBooking, checkInDateFormatted, checkOutDateFormatted)}
                </UitkText>
              </div>
            </UitkLayoutFlex>
          </UitkSpacing>
        </UitkLayoutGridItem>
        <UitkCardLink>
          <TrackedLink moduleName="recently-viewed-hotels" href={hisUrl} rfrr={`card-${cardIndex}`}>
            <LocalizedText message="hotels.hotel.open.page" data={[productInformation?.propertySummary?.name || ""]} />
          </TrackedLink>
        </UitkCardLink>
      </UitkLayoutGrid>
    </UitkCard>
  );
};
